import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import toast from 'react-hot-toast'

// Get API base URL
// For production, always use Railway backend (never use api.synapmarketing.com which doesn't exist)
// For development, use localhost with /api/v1 prefix to match backend route registration
const getAPIBaseURL = (): string => {
  // In production, always use Railway
  if (import.meta.env.PROD) {
    return 'https://marketing-ai-platform-production.up.railway.app/api/v1'
  }

  // In development, try environment variable first, then localhost:4001/api/v1 (backend port with API prefix)
  return import.meta.env.VITE_API_URL || 'http://localhost:4001/api/v1'
}

const API_BASE_URL = getAPIBaseURL()

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add request ID for tracking
    config.headers['X-Request-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data,
      })
    }

    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`, {
        data: response.data,
        headers: response.headers,
      })
    }

    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // Log error in development
    if (import.meta.env.DEV) {
      console.error(`❌ API Error: ${error.response?.status} ${error.config?.url}`, {
        error: error.response?.data,
        message: error.message,
      })
    }

    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response as any

      switch (status) {
        case 401:
          // Unauthorized - Kinde will handle token refresh automatically
          // Just log the user out if token is truly expired
          if (!originalRequest._retry) {
            originalRequest._retry = true

            // Don't show toast for onboarding or certain endpoints that work without auth
            const silentAuthEndpoints = ['/onboarding', '/health', '/status', '/creatives', '/brands', '/agencies', '/agents']
            const shouldSilence = silentAuthEndpoints.some(endpoint => originalRequest.url?.includes(endpoint))

            if (!shouldSilence && !originalRequest.url?.includes('/auth/')) {
              console.warn('401 Unauthorized:', originalRequest.url)
              // Don't force logout or show error - let the user continue browsing with optionalAuth
            }
          }
          break

        case 403:
          // Forbidden — silenced. Auth issues are handled by ProtectedRoute redirect.
          // Showing toast for every 403 spams the user during auth initialization.
          break

        case 404:
          // Not found - suppress for certain endpoints
          const silent404Endpoints = [
            '/health',
            '/cdp',
            '/analytics',
            '/insights',
            '/notifications',
            '/decisions',
            '/creatives',
            '/orchestrator',
            '/dashboard',
            '/brands'
          ]

          const shouldSilence404 = silent404Endpoints.some(endpoint =>
            originalRequest.url?.includes(endpoint)
          )

          if (!shouldSilence404) {
            toast.error(data?.error?.message || 'The requested resource was not found.')
          }
          break

        case 422:
          // Validation error
          const validationMessage = data?.error?.message || 'Please check your input and try again.'
          toast.error(validationMessage)
          break

        case 429:
          // Rate limit exceeded
          toast.error(data?.error?.message || 'Too many requests. Please wait a moment and try again.')
          break

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors - suppress for certain endpoints
          const silentEndpoints = [
            '/cdp',
            '/analytics',
            '/insights',
            '/notifications',
            '/decisions',
            '/health',
            '/orchestrator',
            '/dashboard',
            '/brands'
          ]

          const shouldSilence = silentEndpoints.some(endpoint =>
            originalRequest.url?.includes(endpoint)
          )

          if (!shouldSilence) {
            toast.error(data?.error?.message || 'A server error occurred. Please try again later.')
          }
          break

        default:
          // Generic error
          if (status >= 400) {
            toast.error(data?.error?.message || 'An unexpected error occurred.')
          }
      }
    } else if (error.request) {
      // Network error
      console.error('Network error:', error.message)
      toast.error('Network error. Please check your connection and try again.')
    } else {
      // Request setup error
      console.error('Request setup error:', error.message)
      toast.error('An unexpected error occurred while making the request.')
    }

    return Promise.reject(error)
  }
)

// API client with typed methods
export class ApiClient {
  private instance: AxiosInstance

  constructor(instance: AxiosInstance) {
    this.instance = instance
  }

  // Generic GET request
  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config)
  }

  // Generic POST request
  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config)
  }

  // Generic PUT request
  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config)
  }

  // Generic PATCH request
  async patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data, config)
  }

  // Generic DELETE request
  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config)
  }

  // Health check endpoint
  async healthCheck(): Promise<AxiosResponse<HealthCheckResponse>> {
    return this.get<HealthCheckResponse>('/health')
  }

  // Gateway status check
  async gatewayStatus(): Promise<AxiosResponse<GatewayStatusResponse>> {
    return this.get<GatewayStatusResponse>('/gateway/status')
  }

  // Service discovery
  async serviceDiscovery(): Promise<AxiosResponse<ServiceDiscoveryResponse>> {
    return this.get<ServiceDiscoveryResponse>('/gateway/services')
  }

  // Deep brand research
  async deepResearchBrand(data: {
    brandId: string
    brandName: string
    website: string
    industry?: string
  }): Promise<AxiosResponse<any>> {
    return this.post('/ai/deep-research-brand', data, {
      timeout: 60000 // 60 seconds for AI processing
    })
  }

  // Get brand intelligence
  async getBrandIntelligence(brandId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/intelligence`)
  }

  // Run deep research on brand
  async runDeepResearch(brandId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/research`, {}, {
      timeout: 120000 // 2 minutes for website scraping and AI analysis
    })
  }

  // ===================================
  // Competitor Management
  // ===================================

  // AI-powered competitor discovery
  async discoverCompetitors(brandId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/competitors/discover`, {}, {
      timeout: 60000 // 60 seconds for AI processing
    })
  }

  // List all competitors for a brand
  async getCompetitors(
    brandId: string,
    filters?: { status?: string; relationship?: string }
  ): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/competitors`, { params: filters })
  }

  // Add competitor manually
  async addCompetitor(
    brandId: string,
    data: {
      name: string
      website?: string
      description?: string
      relationship?: 'direct' | 'indirect' | 'substitute' | 'potential'
    }
  ): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/competitors`, data)
  }

  // Get specific competitor
  async getCompetitor(brandId: string, competitorId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/competitors/${competitorId}`)
  }

  // Research competitor website (AI-powered deep analysis)
  async researchCompetitor(brandId: string, competitorId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/competitors/${competitorId}/research`, {}, {
      timeout: 90000 // 90 seconds for website scraping and AI analysis
    })
  }

  // Generate competitive comparison
  async compareWithCompetitor(brandId: string, competitorId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/competitors/${competitorId}/compare`, {}, {
      timeout: 60000 // 60 seconds for AI comparison
    })
  }

  // Update competitor
  async updateCompetitor(
    brandId: string,
    competitorId: string,
    data: {
      name?: string
      website?: string
      description?: string
      relationship?: string
      status?: string
    }
  ): Promise<AxiosResponse<any>> {
    return this.patch(`/brands/${brandId}/competitors/${competitorId}`, data)
  }

  // Remove competitor
  async removeCompetitor(brandId: string, competitorId: string): Promise<AxiosResponse<any>> {
    return this.delete(`/brands/${brandId}/competitors/${competitorId}`)
  }

  // ===================================
  // SWOT Analysis
  // ===================================

  // Generate SWOT analysis
  async generateSWOT(brandId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/swot`, {}, {
      timeout: 60000 // 60 seconds for AI analysis
    })
  }

  // Get existing SWOT analysis
  async getSWOT(brandId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/swot`)
  }

  // Regenerate SWOT analysis
  async regenerateSWOT(brandId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/swot/regenerate`, {}, {
      timeout: 60000 // 60 seconds for AI analysis
    })
  }

  // ===================================
  // Market Forces (Porter's Five Forces)
  // ===================================

  // Analyze market forces
  async analyzeMarketForces(brandId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/market-forces`, {}, {
      timeout: 60000 // 60 seconds for AI analysis
    })
  }

  // Get existing market forces analysis
  async getMarketForces(brandId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/market-forces`)
  }

  // Regenerate market forces analysis
  async regenerateMarketForces(brandId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/market-forces/regenerate`, {}, {
      timeout: 60000 // 60 seconds for AI analysis
    })
  }

  // ===================================
  // Sentiment Analysis & Brand Perception
  // ===================================

  // Analyze sentiment from specific source
  async analyzeSentiment(
    brandId: string,
    source: 'reviews' | 'social_media' | 'news' | 'surveys'
  ): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/sentiment/${source}`, {}, {
      timeout: 60000 // 60 seconds for AI analysis
    })
  }

  // Analyze sentiment from all sources
  async analyzeSentimentAllSources(brandId: string): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/sentiment/all`, {}, {
      timeout: 120000 // 2 minutes for multiple AI analyses
    })
  }

  // Get sentiment history
  async getSentimentHistory(brandId: string, source?: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/sentiment`, {
      params: source ? { source } : undefined
    })
  }

  // Get aggregated sentiment summary
  async getSentimentSummary(brandId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/sentiment/summary`)
  }

  // ===================================
  // Product Research
  // ===================================

  // Get AI-generated products for a brand
  async getProducts(brandId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/products`)
  }

  // Run product research
  async runProductResearch(brandId: string, forceRefresh: boolean = false): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/research/products`, { forceRefresh }, {
      timeout: 120000 // 2 minutes for AI analysis
    })
  }

  // ===================================
  // Persona/ICP Research
  // ===================================

  // Get personas for a brand
  async getPersonas(brandId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/personas`)
  }

  // Generate personas
  async generatePersonas(brandId: string, options?: { personaCount?: number }): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/personas/generate`, options || {}, {
      timeout: 120000 // 2 minutes for AI analysis
    })
  }

  // ===================================
  // Pricing Research
  // ===================================

  // Get pricing research for a brand
  async getPricingResearch(brandId: string): Promise<AxiosResponse<any>> {
    return this.get(`/brands/${brandId}/pricing`)
  }

  // Run pricing research
  async runPricingResearch(brandId: string, forceRefresh: boolean = false): Promise<AxiosResponse<any>> {
    return this.post(`/brands/${brandId}/research/pricing`, { forceRefresh }, {
      timeout: 120000 // 2 minutes for AI analysis
    })
  }

  // ===================================
  // Customer Data Platform (CDP)
  // ===================================

  // Get CDP customer segments
  async getCDPSegments(): Promise<AxiosResponse<any>> {
    return this.get('/cdp/segments')
  }

  // Get CDP customer profiles
  async getCDPProfiles(brandId: string, params?: { limit?: number; offset?: number }): Promise<AxiosResponse<any>> {
    return this.get(`/cdp/${brandId}/profiles`, { params })
  }

  // Get CDP customer 360 view
  async getCDPCustomer360(brandId: string, profileId: string): Promise<AxiosResponse<any>> {
    return this.get(`/cdp/${brandId}/profiles/${profileId}/360`)
  }

  // ===================================
  // Campaign Briefs
  // ===================================

  // Generate a new campaign brief
  async generateBrief(input: any): Promise<AxiosResponse<any>> {
    return this.post('/briefs/generate', input, {
      timeout: 90000 // 90 seconds for AI generation
    })
  }

  // Get all briefs
  async getBriefs(filters?: { status?: string; campaignId?: string }): Promise<AxiosResponse<any>> {
    return this.get('/briefs', { params: filters })
  }

  // Get a specific brief
  async getBrief(id: string): Promise<AxiosResponse<any>> {
    return this.get(`/briefs/${id}`)
  }

  // Regenerate a section of a brief
  async regenerateBriefSection(id: string, section: string): Promise<AxiosResponse<any>> {
    return this.post(`/briefs/${id}/regenerate-section`, { section }, {
      timeout: 60000 // 60 seconds for AI regeneration
    })
  }

  // Update a brief (draft only)
  async updateBrief(id: string, data: any): Promise<AxiosResponse<any>> {
    return this.put(`/briefs/${id}`, data)
  }

  // Approve a brief
  async approveBrief(id: string, approvedBy?: string): Promise<AxiosResponse<any>> {
    return this.put(`/briefs/${id}/approve`, { approvedBy })
  }

  // Reject a brief
  async rejectBrief(id: string, reason?: string): Promise<AxiosResponse<any>> {
    return this.put(`/briefs/${id}/reject`, { reason })
  }

  // Delete a brief (draft only)
  async deleteBrief(id: string): Promise<AxiosResponse<any>> {
    return this.delete(`/briefs/${id}`)
  }

  // Export brief as PDF
  async exportBriefPDF(id: string): Promise<AxiosResponse<any>> {
    return this.get(`/briefs/${id}/export-pdf`)
  }

  // ===================================
  // Campaign Versioning
  // ===================================

  // Create a new campaign version
  async createCampaignVersion(campaignId: string, data: {
    changeType: string;
    changeDescription?: string;
    createdBy?: string;
  }): Promise<AxiosResponse<any>> {
    return this.post(`/campaigns/${campaignId}/versions`, data)
  }

  // Get all versions for a campaign
  async getCampaignVersions(campaignId: string): Promise<AxiosResponse<any>> {
    return this.get(`/campaigns/${campaignId}/versions`)
  }

  // Get a specific campaign version
  async getCampaignVersion(campaignId: string, versionId: string): Promise<AxiosResponse<any>> {
    return this.get(`/campaigns/${campaignId}/versions/${versionId}`)
  }

  // Compare two campaign versions
  async compareCampaignVersions(
    campaignId: string,
    versionId1: string,
    versionId2: string
  ): Promise<AxiosResponse<any>> {
    return this.get(`/campaigns/${campaignId}/versions/compare/${versionId1}/${versionId2}`)
  }

  // Restore a campaign to a specific version
  async restoreCampaignVersion(
    campaignId: string,
    versionId: string,
    restoredBy?: string
  ): Promise<AxiosResponse<any>> {
    return this.post(`/campaigns/${campaignId}/versions/${versionId}/restore`, { restoredBy })
  }

  // Delete a campaign version
  async deleteCampaignVersion(campaignId: string, versionId: string): Promise<AxiosResponse<any>> {
    return this.delete(`/campaigns/${campaignId}/versions/${versionId}`)
  }

  // ===================================
  // ML Predictions & AI Insights
  // ===================================

  // Get ML forecast for agent performance
  async getAgentForecast(
    agentId: string,
    params?: {
      metric?: 'revenue' | 'conversions' | 'success_rate' | 'engagement_rate' | 'roi'
      forecast_days?: number
      historical_days?: number
    }
  ): Promise<AxiosResponse<any>> {
    return this.get(`/ml-predictions/agent/${agentId}/forecast`, { params })
  }

  // Get AI insights and recommendations for agent
  async getAgentInsights(agentId: string): Promise<AxiosResponse<any>> {
    return this.get(`/ml-predictions/agent/${agentId}/insights`)
  }

  // Trigger ML model retraining
  async trainMLModels(data?: {
    agent_ids?: string[]
    metrics?: string[]
    training_window_days?: number
  }): Promise<AxiosResponse<any>> {
    return this.post('/ml-predictions/train', data, {
      timeout: 300000 // 5 minutes for model training
    })
  }

  // Get ML model status
  async getMLModelStatus(): Promise<AxiosResponse<any>> {
    return this.get('/ml-predictions/model-status')
  }
}

// Response type definitions
export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
    details?: any
  }
  metadata?: {
    timestamp: string
    request_id: string
    version: string
  }
}

export interface PaginatedApiResponse<T = any> extends ApiResponse<T[]> {
  pagination?: {
    page: number
    limit: number
    total: number
    pages: number
    has_next: boolean
    has_prev: boolean
  }
}

export interface HealthCheckResponse {
  status: string
  timestamp: string
  version: string
  uptime: number
  services: Record<string, boolean>
  memory: NodeJS.MemoryUsage
  environment: string
}

export interface GatewayStatusResponse {
  gateway: {
    name: string
    version: string
    environment: string
    uptime: number
    started_at: string
  }
  services: Record<string, any>
  configuration: {
    cors_origins: string[]
    rate_limit: {
      windowMs: number
      max: number
    }
    auth_enabled: boolean
  }
}

export interface ServiceDiscoveryResponse {
  services: Array<{
    path: string
    name: string
    url: string
    healthy: boolean
    timeout: number
    retries: number
  }>
  total_services: number
  healthy_services: number
}

// Create and export the API client instance
export const apiClient = new ApiClient(api)

// Export the axios instance as default
export default api